import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "react-hot-toast";

import { AppLayout } from "@/components/shared/AppLayout";
import { SupportChatBridge } from "@/components/support/SupportChatBridge";
import { OnboardingLayout } from "@/components/shared/OnboardingLayout";
import { PortalDocumentTitle } from "@/components/shared/PortalDocumentTitle";
import { ProtectedRoute } from "@/components/shared/ProtectedRoute";
import { RootRedirect } from "@/components/shared/RootRedirect";
import { PaidPlanRoute, UltraPlanRoute } from "@/components/shared/PaidPlanRoute";
import { TenantRequiredRoute } from "@/components/shared/TenantRequiredRoute";
import { TenantsRouteGuard } from "@/components/shared/TenantsRouteGuard";
import { PortalThemeProvider } from "@/components/shared/PortalThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { OnboardingProgressProvider } from "@/contexts/OnboardingProgressContext";
import { TenantSwitcherProvider } from "@/contexts/TenantSwitcherContext";
import { LoginPage } from "@/pages/auth/login-page";
import { RegisterPage } from "@/pages/auth/register-page";
import { VerifyPage } from "@/pages/auth/verify-page";
import { VerifyRootInvitePage } from "@/pages/auth/verify-root-invite-page";
import { VerifyEmailPage } from "@/pages/auth/verify-email-page";
import { SsoCallbackPage } from "@/pages/auth/sso-callback-page";
import { ForgotPasswordPage } from "@/pages/auth/forgot-password-page";
import { ResetPasswordPage } from "@/pages/auth/reset-password-page";
import { AcceptInvitationPage } from "@/pages/auth/accept-invitation-page";
import { DlocalReturnPage } from "@/pages/pay/dlocal-return-page";
import { MercadoPagoPage } from "@/pages/pay/mercadopago-page";
import { ThankYouPage } from "@/pages/pay/thank-you-page";
import { LinkNotAvailablePage } from "@/pages/pay/link-not-available-page";
import { AccountantHomePage } from "@/pages/accountant/accountant-home-page";
import { AccountantProfilePage } from "@/pages/accountant/accountant-profile-page";
import { AccountantRequestsPage } from "@/pages/accountant/accountant-requests-page";
import { SolicitudDetailPage } from "@/pages/solicitudes/solicitud-detail-page";
import { DashboardPage } from "@/pages/dashboard/dashboard-page";
import { OnboardingPage } from "@/pages/onboarding/onboarding-page";
import { AccountantOnboardingPage } from "@/pages/onboarding/accountant-onboarding-page";
import { AccountantNewClientPage } from "@/pages/accountant/accountant-new-client-page";
import { AccountantClientOnboardingPage } from "@/pages/accountant/accountant-client-onboarding-page";
import { AccountantClientDetailPage } from "@/pages/accountant/accountant-client-detail-page";
import { AccountantClientSubdiarioPage } from "@/pages/accountant/accountant-client-subdiario-page";
import { AccountantClientIvaPeriodPage } from "@/pages/accountant/accountant-client-iva-period-page";
import { BandejaPage } from "@/pages/accountant/bandeja-page";
import { BandejaAcksPage } from "@/pages/accountant/bandeja-acks-page";
import { BandejaBoletosPage } from "@/pages/accountant/bandeja-boletos-page";
import { BandejaObligationsPage } from "@/pages/accountant/bandeja-obligations-page";
import { CreditNoteWizardPage } from "@/pages/invoices/credit-note-wizard-page";
import { DebitNoteWizardPage } from "@/pages/invoices/debit-note-wizard-page";
import { InvoiceDetailPage } from "@/pages/invoices/invoice-detail-page";
import { InvoicesPage } from "@/pages/invoices/invoices-page";
import { RecibidasPage } from "@/pages/invoices/recibidas-page";
import { RecibidaDetailPage } from "@/pages/invoices/recibida-detail-page";
import { DailyReportsPage } from "@/pages/invoices/daily-reports-page";
import { DailyReportDetailPage } from "@/pages/invoices/daily-report-detail-page";
import { NewInvoicePage } from "@/pages/invoices/new-invoice-page";
import { RecurringInvoicesListPage } from "@/pages/invoices/recurring-invoices-list-page";
import { RecurringInvoiceFormPage } from "@/pages/invoices/recurring-invoice-form-page";
import { ClientsPage } from "@/pages/clients/clients-page";
import { ProductsPage } from "@/pages/products/products-page";
import { NewClientPage } from "@/pages/clients/new-client-page";
import { EditClientPage } from "@/pages/clients/edit-client-page";
import { ClientDetailsPage } from "@/pages/clients/client-details-page";
import { SuppliersPage } from "@/pages/suppliers/suppliers-page";
import { NewSupplierPage } from "@/pages/suppliers/new-supplier-page";
import { EditSupplierPage } from "@/pages/suppliers/edit-supplier-page";
import { SupplierDetailsPage } from "@/pages/suppliers/supplier-details-page";
import { DocumentsPage } from "@/pages/documents/documents-page";
import { ExpensesPage } from "@/pages/expenses/expenses-page";
import { NewExpensePage } from "@/pages/expenses/new-expense-page";
import { DgiDocumentsPage } from "@/pages/dgi-documents/dgi-documents-page";
import { EstadoFiscalPage } from "@/pages/estado-fiscal/estado-fiscal-page";
import ReportsPage from "@/pages/reports/reports-page";
import { FiscalAgendaPage } from "@/pages/fiscal-agenda/fiscal-agenda-page";
import { ProfileEditPage } from "@/pages/profile/profile-edit-page";
import { ProfilePage } from "@/pages/profile/profile-page";
import { SettingsPage } from "@/pages/settings/settings-page";
import { AddCompanyPage } from "@/pages/settings/add-company-page";
import { TenantsPage } from "@/pages/tenants/tenants-page";
import { TenantDetailPage } from "@/pages/tenants/tenant-detail-page";
import { EditTenantPage } from "@/pages/tenants/edit-tenant-page";
import { InviteDetailPage } from "@/pages/users/invite-detail-page";
import { UserDetailPage } from "@/pages/users/user-detail-page";
import { UsersPage } from "@/pages/users/users-page";
import { FeedbackPage } from "@/pages/feedback/feedback-page";
import { CertificateRequestsPage } from "@/pages/certificate-requests/certificate-requests-page";
import { PlatformHomologacionesPage } from "@/pages/platform-homologaciones/platform-homologaciones-page";
import { PlatformHomologacionDetailPage } from "@/pages/platform-homologaciones/platform-homologacion-detail-page";
import { CertificateRequestDetailPage } from "@/pages/certificate-requests/certificate-request-detail-page";
import { ProveedorSoftwarePage } from "@/pages/proveedor-software/proveedor-software-page";
import { PlatformConfigPage } from "@/pages/platform-config/platform-config-page";
import { PlatformMetricsPage } from "@/pages/platform-metrics/platform-metrics-page";
import { PlatformAnalyticsPage } from "@/pages/platform-analytics/platform-analytics-page";
import { PlatformSupportPage } from "@/pages/platform-support/platform-support-page";
import { PlatformFiscalBenefitPage } from "@/pages/platform-fiscal-benefit/platform-fiscal-benefit-page";
import { BankTransferReviewsPage } from "@/pages/bank-transfer-reviews/bank-transfer-reviews-page";
import { PartnersPage } from "@/pages/partners/partners-page";
import { RequestsListRoute } from "@/components/shared/RequestsRoute";
import { RequestsDetailRoute } from "@/components/shared/RequestsRoute";
import { RouteAnalytics } from "@/lib/analytics/RouteAnalytics";
import { startAnalytics } from "@/lib/analytics";
import { MagScopeGuard } from "@/lib/magScopeGuard";
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

// Starts the analytics flush timer + unload hook. Idempotent, and safe at
// module scope: it only registers listeners, and emits nothing until a user
// authenticates.
startAnalytics();

function App(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <PortalThemeProvider>
            <PortalDocumentTitle />
            <RouteAnalytics />
            <MagScopeGuard />
            <Toaster
              position="top-center"
              containerStyle={{ top: 16, zIndex: 99999 }}
              toastOptions={{
                // Brand-aligned editorial toast — dark ink-900 surface,
                // cream text, sage-300 accent for success, warm warn for
                // errors. Pill radius + subtle drop shadow that matches
                // the ef-hero treatment used on the dashboard.
                style: {
                  // The toast is a fixed dark pill in BOTH themes — the same
                  // treatment as the sidebar. The pair is self-consistent, so
                  // it must NOT follow the surface tokens.
                  // eslint-disable-next-line no-restricted-syntax
                  background: "var(--fx-ink-900)",
                  // eslint-disable-next-line no-restricted-syntax
                  color: "var(--fx-paper)",
                  borderRadius: "var(--fx-r-md)",
                  border: "1px solid rgba(244,239,230,0.08)",
                  boxShadow: "0 18px 44px -28px rgba(22,21,20,0.45)",
                  padding: "12px 16px",
                  fontSize: 14,
                  fontWeight: 500,
                  letterSpacing: "-0.005em",
                  fontFamily: "var(--fx-ff-sans)",
                  maxWidth: 480,
                },
                success: {
                  iconTheme: {
                    primary: "var(--fx-accent-300)",
                    secondary: "var(--fx-ink-900)",
                  },
                },
                error: {
                  iconTheme: {
                    primary: "var(--fx-warn, #C95E3E)",
                    secondary: "var(--fx-ink-900)",
                  },
                },
              }}
            />
            <SupportChatBridge />
            <TooltipProvider delayDuration={250}>
              <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/verify" element={<VerifyPage />} />
          <Route path="/auth/verify-root-invite" element={<VerifyRootInvitePage />} />
          <Route
            path="/verify-email"
            element={
              <ProtectedRoute>
                <VerifyEmailPage />
              </ProtectedRoute>
            }
          />
          <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
          <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route
            path="/accept-invitation"
            element={<AcceptInvitationPage />}
          />
          <Route
            path="/accept-invitation/:token"
            element={<AcceptInvitationPage />}
          />
          <Route path="/pay/dlocal-return" element={<DlocalReturnPage />} />
          <Route path="/pay/mercadopago" element={<MercadoPagoPage />} />
          <Route path="/pay/thank-you" element={<ThankYouPage />} />
          <Route
            path="/pay/link-not-available"
            element={<LinkNotAvailablePage />}
          />
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <OnboardingProgressProvider>
                    <OnboardingLayout />
                  </OnboardingProgressProvider>
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          >
            <Route index element={<OnboardingPage />} />
          </Route>
          <Route
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <OnboardingLayout />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          >
            <Route
              path="/onboarding/accountant"
              element={<AccountantOnboardingPage />}
            />
            <Route
              path="/accountant/new-client"
              element={<AccountantNewClientPage />}
            />
            <Route
              path="/accountant/client/:tenantId/onboarding"
              element={<AccountantClientOnboardingPage />}
            />
          </Route>
          <Route
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <AppLayout />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <TenantRequiredRoute>
                  <DashboardPage />
                </TenantRequiredRoute>
              }
            />
            <Route path="/accountant" element={<AccountantHomePage />} />
            <Route
              path="/accountant/inbox"
              element={<BandejaPage />}
            />
            <Route
              path="/accountant/inbox/acks"
              element={<BandejaAcksPage />}
            />
            <Route
              path="/accountant/inbox/boletos"
              element={<BandejaBoletosPage />}
            />
            <Route
              path="/accountant/inbox/obligations"
              element={<BandejaObligationsPage />}
            />
            <Route
              path="/accountant/client/:tenantId"
              element={<AccountantClientDetailPage />}
            />
            <Route
              path="/accountant/client/:tenantId/subdiario/:period"
              element={<AccountantClientSubdiarioPage />}
            />
            <Route
              path="/accountant/client/:tenantId/iva/:period"
              element={<AccountantClientIvaPeriodPage />}
            />
            <Route
              path="/accountant/profile"
              element={<AccountantProfilePage />}
            />
            <Route
              path="/accountant/requests"
              element={<AccountantRequestsPage />}
            />
            <Route
              path="/accountant/requests/:id"
              element={<SolicitudDetailPage />}
            />
            <Route path="/requests" element={<RequestsListRoute />} />
            <Route path="/requests/:id" element={<RequestsDetailRoute />} />
            <Route
              path="/invoices"
              element={
                <TenantRequiredRoute>
                  <InvoicesPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/recibidas"
              element={
                <TenantRequiredRoute>
                  <RecibidasPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/recibidas/:id"
              element={
                <TenantRequiredRoute>
                  <RecibidaDetailPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/dgi/daily-reports"
              element={
                <TenantRequiredRoute>
                  <DailyReportsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/dgi/daily-reports/:reportId"
              element={
                <TenantRequiredRoute>
                  <DailyReportDetailPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/new"
              element={
                <TenantRequiredRoute>
                  <NewInvoicePage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/recurring"
              element={
                <TenantRequiredRoute>
                  <RecurringInvoicesListPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/recurring/new"
              element={
                <TenantRequiredRoute>
                  <RecurringInvoiceFormPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/recurring/:id/edit"
              element={
                <TenantRequiredRoute>
                  <RecurringInvoiceFormPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <TenantRequiredRoute>
                  <InvoiceDetailPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/:id/credit-note"
              element={
                <TenantRequiredRoute>
                  <CreditNoteWizardPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/invoices/:id/debit-note"
              element={
                <TenantRequiredRoute>
                  <DebitNoteWizardPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <TenantRequiredRoute>
                  <ClientsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/products"
              element={
                <TenantRequiredRoute>
                  <ProductsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/clients/new"
              element={
                <TenantRequiredRoute>
                  <NewClientPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/clients/:id/edit"
              element={
                <TenantRequiredRoute>
                  <EditClientPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/clients/:id"
              element={
                <TenantRequiredRoute>
                  <ClientDetailsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/suppliers"
              element={
                <TenantRequiredRoute>
                  <SuppliersPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/suppliers/new"
              element={
                <TenantRequiredRoute>
                  <NewSupplierPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/suppliers/:id/edit"
              element={
                <TenantRequiredRoute>
                  <EditSupplierPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/suppliers/:id"
              element={
                <TenantRequiredRoute>
                  <SupplierDetailsPage />
                </TenantRequiredRoute>
              }
            />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/profile/edit" element={<ProfileEditPage />} />
            <Route
              path="/estado-fiscal"
              element={<Navigate to="/fiscal-status" replace />}
            />
            <Route
              path="/fiscal-status"
              element={
                <TenantRequiredRoute>
                  <PaidPlanRoute>
                    <EstadoFiscalPage />
                  </PaidPlanRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/agenda"
              element={
                <TenantRequiredRoute>
                  <PaidPlanRoute>
                    <FiscalAgendaPage />
                  </PaidPlanRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/documents"
              element={
                <TenantRequiredRoute>
                  <PaidPlanRoute>
                    <DocumentsPage />
                  </PaidPlanRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <TenantRequiredRoute>
                  <PaidPlanRoute>
                    <UltraPlanRoute>
                      <ReportsPage />
                    </UltraPlanRoute>
                  </PaidPlanRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <TenantRequiredRoute>
                  <ExpensesPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/expenses/new"
              element={
                <TenantRequiredRoute>
                  <NewExpensePage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/dgi-documents"
              element={
                <TenantRequiredRoute>
                  <PaidPlanRoute>
                    <UltraPlanRoute>
                      <DgiDocumentsPage />
                    </UltraPlanRoute>
                  </PaidPlanRoute>
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <TenantRequiredRoute>
                  <SettingsPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/settings/add-company"
              element={
                <TenantRequiredRoute>
                  <AddCompanyPage />
                </TenantRequiredRoute>
              }
            />
            <Route
              path="/tenants"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <TenantsRouteGuard>
                    <TenantsPage />
                  </TenantsRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/feedback"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <FeedbackPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/proveedor-software"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <ProveedorSoftwarePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-config"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformConfigPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-analytics"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-metrics"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformMetricsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-support"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformSupportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/platform-fiscal-benefit"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformFiscalBenefitPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bank-transfer-reviews"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <BankTransferReviewsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/partners"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PartnersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-requests"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificateRequestsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/homologaciones"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformHomologacionesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/homologaciones/:companyId"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <PlatformHomologacionDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificate-requests/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <CertificateRequestDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants/:id/edit"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <TenantsRouteGuard>
                    <EditTenantPage />
                  </TenantsRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tenants/:id"
              element={
                <ProtectedRoute allowedRoles={["root", "root_admin"]}>
                  <TenantsRouteGuard>
                    <TenantDetailPage />
                  </TenantsRouteGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/invites/:inviteId"
              element={
                <ProtectedRoute>
                  <InviteDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:userId"
              element={
                <ProtectedRoute>
                  <UserDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
          </Route>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <RootRedirect />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <TenantSwitcherProvider>
                  <Navigate to="/" replace />
                </TenantSwitcherProvider>
              </ProtectedRoute>
            }
          />
              </Routes>
            </TooltipProvider>
          </PortalThemeProvider>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
